/**
 * Pure guard for the global single-key shortcuts (useGlobalShortcuts.web.ts).
 * Kept in its own platform-agnostic module (no DOM/React/store imports) so
 * it's importable unambiguously from a jest test regardless of which
 * platform extension Jest's resolver would otherwise prefer for a `.web.ts`
 * sibling — see __tests__/hooks/shortcutGuard.test.ts.
 */

/** The subset of a real KeyboardEvent the guard needs — lets tests pass a
 *  plain object instead of constructing a DOM event. */
export interface ShortcutKeyboardEvent {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  repeat: boolean;
  target: EventTarget | null;
}

export interface ShortcutGuardState {
  /** The fullscreen/panel observation presentation is up — it owns its own
   *  keys (useSlideshowKeys.ts: Escape/arrows/space). */
  isPresenting: boolean;
  /** A defense-plan draw tool is armed — the draw gesture owns Enter/Escape
   *  while active (useDefensePlanDraw.web.ts). */
  isDrawingPlan: boolean;
  /** Any dialog or sheet is on top of the escape stack (utils/escapeStack) —
   *  its own controls should get the keystroke, not a global shortcut. */
  isDialogOpen: boolean;
}

const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  return TYPING_TAGS.has(el.tagName);
}

/**
 * True when a global single-key shortcut (L/K/P/N/?//) should act on this
 * keydown. False for: a repeat (held key); any modifier other than Shift
 * (Shift is needed to type "?"); focus in a text field; the presentation up;
 * a dialog/sheet open; or a defense-plan draw tool armed.
 */
export function shouldHandleShortcut(
  event: ShortcutKeyboardEvent,
  state: ShortcutGuardState
): boolean {
  if (event.repeat) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (isTypingTarget(event.target)) return false;
  if (state.isPresenting) return false;
  if (state.isDialogOpen) return false;
  if (state.isDrawingPlan) return false;
  return true;
}
